import { useCallback, useEffect, useState } from 'react';
import { Box, Button, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import ShoppingCartRounded from '@mui/icons-material/ShoppingCartRounded';
import Swal from 'sweetalert2';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkSelectBar from '../components/ui/BulkSelectBar';
import PaginationBar from '../components/ui/PaginationBar';
import { usePagination } from '../hooks/usePagination';
import { listGroceryItems, getGroceryStats, toggleGroceryStatus, updateGroceryItem, deleteGroceryItem, GroceryItemRow } from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import TabFilterBar from '../components/ui/TabFilterBar';
import EmptyState from '../components/ui/EmptyState';
import GroceryItemFormDialog from '../components/grocery/GroceryItemFormDialog';
import GroceryCards from '../components/grocery/GroceryCards';
import ViewToggle, { useViewMode } from '../components/ui/ViewToggle';

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'to_buy', label: 'To Buy' },
  { value: 'bought', label: 'Bought' },
  { value: 'favourites', label: 'Favourites' },
];

const GroceryPage = () => {
  const [tab, setTab] = useState('all');
  const [items, setItems] = useState<GroceryItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, bought: 0, toBuy: 0, percentBought: 0 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<GroceryItemRow | null>(null);
  const bulk = useBulkSelect();
  const [view, setView] = useViewMode('quillstack.grocery.view', 'grid');
  const pager = usePagination(items);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = tab === 'favourites' ? { favourite: true } : tab === 'all' ? {} : { status: tab };
      const [list, s] = await Promise.all([listGroceryItems(params), getGroceryStats()]);
      setItems(list);
      setStats(s);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (item: GroceryItemRow) => {
    await toggleGroceryStatus(item._id);
    load();
  };

  const handleToggleFavourite = async (item: GroceryItemRow) => {
    await updateGroceryItem(item._id, { favourite: !item.favourite });
    load();
  };

  const handleDelete = async (item: GroceryItemRow) => {
    const result = await Swal.fire({ title: 'Delete item?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete' });
    if (!result.isConfirmed) return;
    await deleteGroceryItem(item._id);
    load();
  };

  return (
    <Box>
      <PageHeader
        title="Grocery"
        actions={
          <Button startIcon={<AddRounded />} variant="contained" onClick={() => { setEditing(null); setDialogOpen(true); }}>
            Add Item
          </Button>
        }
      />

      {/* Progress strip */}
      <Paper sx={{ p: 2, mb: 2.5, borderRadius: '20px' }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
              <Typography sx={{ fontWeight: 800 }}>Shopping progress</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {stats.bought} of {stats.total} bought · {stats.toBuy} to buy
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={stats.percentBought}
              sx={{
                height: 10, borderRadius: 999, bgcolor: 'rgba(255,152,0,0.16)',
                '& .MuiLinearProgress-bar': { borderRadius: 999, background: 'linear-gradient(90deg, #FF9800, #E65100)' },
              }}
            />
          </Box>
          <Typography sx={{ fontWeight: 900, fontSize: 26, color: '#E65100', minWidth: 64, textAlign: 'right' }}>
            {stats.percentBought}%
          </Typography>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1, mb: 2.5 }}>
        <TabFilterBar value={tab} onChange={setTab} options={TABS} noMargin />
        <Box sx={{ flex: 1 }} />
        <ViewToggle value={view} onChange={setView} />
      </Stack>

      {!loading && items.length === 0 ? (
        <EmptyState icon={ShoppingCartRounded} title="No grocery items" description="Add items you need to buy." actionLabel="Add Item" onAction={() => { setEditing(null); setDialogOpen(true); }} />
      ) : (
        <>
        <BulkSelectBar bulk={bulk} visibleIds={pager.pageRows.map((i) => i._id)} noun="item" deleteOne={deleteGroceryItem} onDone={load} />
        <GroceryCards
          items={pager.pageRows}
          bulk={bulk}
          view={view}
          onToggleBought={handleToggle}
          onToggleFavourite={handleToggleFavourite}
          onEdit={(i) => { setEditing(i); setDialogOpen(true); }}
          onDelete={handleDelete}
        />
        <PaginationBar page={pager.page} totalPages={pager.totalPages} pageSize={pager.pageSize} onPageChange={pager.setPage} onPageSizeChange={pager.setPageSize} />
        </>
      )}

      <GroceryItemFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={load} editing={editing} />
    </Box>
  );
};

export default GroceryPage;
